import React, { useState, useEffect } from 'react'
import { useAuthStore } from '../store/useAuthStore'

const AnalyticsDashboard = () => {
  const { profile } = useAuthStore()
  const [timeRange, setTimeRange] = useState('30d')
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)

  // Mock analytics data
  useEffect(() => {
    const mockAnalytics = {
      revenue: {
        total: 1250000,
        monthly: 450000,
        growth: 12.5
      },
      projects: {
        total: 24,
        active: 8,
        completed: 14,
        pending: 2
      },
      clients: {
        total: 18,
        new: 3,
        active: 12,
        inactive: 3
      },
      employees: {
        total: 6,
        active: 5,
        utilization: 85
      },
      chartData: {
        revenue: [
          { month: 'Jan', amount: 120000 },
          { month: 'Feb', amount: 135000 },
          { month: 'Mar', amount: 150000 },
          { month: 'Apr', amount: 165000 },
          { month: 'May', amount: 180000 },
          { month: 'Jun', amount: 195000 }
        ],
        projects: [
          { month: 'Jan', count: 3 },
          { month: 'Feb', count: 4 },
          { month: 'Mar', count: 5 },
          { month: 'Apr', count: 6 },
          { month: 'May', count: 7 },
          { month: 'Jun', count: 8 }
        ]
      },
      recentActivities: [
        { id: 1, type: 'project', message: 'E-commerce Website project completed', time: '2 hours ago', status: 'success' },
        { id: 2, type: 'payment', message: 'Payment received from John Doe', time: '4 hours ago', status: 'success' },
        { id: 3, type: 'client', message: 'New client registration', time: '6 hours ago', status: 'info' },
        { id: 4, type: 'task', message: 'Mobile app development task completed', time: '8 hours ago', status: 'success' }
      ],
      topClients: [
        { name: 'John Doe', projects: 3, revenue: 450000 },
        { name: 'Jane Smith', projects: 2, revenue: 320000 },
        { name: 'Mike Johnson', projects: 2, revenue: 280000 },
        { name: 'Sarah Wilson', projects: 1, revenue: 150000 }
      ],
      projectStatus: [
        { status: 'Completed', count: 14, percentage: 58 },
        { status: 'Active', count: 8, percentage: 33 },
        { status: 'Pending', count: 2, percentage: 9 }
      ]
    }
    
    setTimeout(() => {
      setAnalytics(mockAnalytics)
      setLoading(false)
    }, 1000)
  }, [timeRange])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="page-container py-8">
          <div className="animate-pulse">
            <div className="bg-gray-200 h-8 w-64 rounded mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-200 h-32 rounded"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-gray-200 h-64 rounded"></div>
              <div className="bg-gray-200 h-64 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="page-container py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="text-gray-600 mt-2">Business insights and performance metrics</p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="input-field"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <button className="btn-primary">Export Report</button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-container py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-semibold text-gray-900">৳{analytics.revenue.total.toLocaleString()}</p>
                <p className="text-sm text-green-600">+{analytics.revenue.growth}% from last month</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Projects</p>
                <p className="text-2xl font-semibold text-gray-900">{analytics.projects.total}</p>
                <p className="text-sm text-gray-600">{analytics.projects.active} active</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Clients</p>
                <p className="text-2xl font-semibold text-gray-900">{analytics.clients.total}</p>
                <p className="text-sm text-gray-600">{analytics.clients.new} new this month</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Team Utilization</p>
                <p className="text-2xl font-semibold text-gray-900">{analytics.employees.utilization}%</p>
                <p className="text-sm text-gray-600">{analytics.employees.active} active members</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts and Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Revenue Chart */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">Revenue Trend</h2>
            <div className="h-64 flex items-end space-x-2">
              {analytics.chartData.revenue.map((item, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div
                    className="bg-primary-600 rounded-t w-full mb-2"
                    style={{ height: `${(item.amount / 200000) * 200}px` }}
                  ></div>
                  <span className="text-xs text-gray-600">{item.month}</span>
                  <span className="text-xs font-medium">৳{(item.amount / 1000).toFixed(0)}k</span>
                </div>
              ))}
            </div>
          </div>

          {/* Project Status */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">Project Status Distribution</h2>
            <div className="space-y-4">
              {analytics.projectStatus.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{item.status}</span>
                    <span className="text-sm text-gray-600">{item.count} projects</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        index === 0 ? 'bg-green-500' : index === 1 ? 'bg-blue-500' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Activities */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">Recent Activities</h2>
            <div className="space-y-3">
              {analytics.recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start">
                  <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${
                    activity.status === 'success' ? 'bg-green-500' :
                    activity.status === 'info' ? 'bg-blue-500' : 'bg-yellow-500'
                  }`}></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.message}</p>
                    <p className="text-xs text-gray-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Clients */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">Top Clients</h2>
            <div className="space-y-3">
              {analytics.topClients.map((client, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-900">{client.name}</p>
                    <p className="text-sm text-gray-600">{client.projects} projects</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">৳{client.revenue.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsDashboard
